import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { UseQueryResult, useQuery, useQueryClient } from 'react-query';
import { Merchant, Processor, getMerchants } from '@frontend/api-payments';
import { isWeaveUser } from '@frontend/auth-helpers';
import { useAppScopeStore, WeaveLocation } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { getInterScreenQueryOptions, paymentsQueryKeys } from '../../utils/query-helpers';
import { LocationIdsByType } from '../locations/use-location-ids-by-type';
import { MerchantLocationSelectionType, useMerchantLocationIds } from './use-merchant-location-ids';

export type UserMerchantsOptions = {
  enabled?: boolean;
};

export type MerchantResult = UseQueryResult<Record<string, Merchant>, unknown>;

type QueryResultInfo = { isLoading: boolean; error?: unknown; isFetched: boolean; refetch: () => void };

export const getMerchantInfo = (
  merchant: Merchant | undefined,
  queryInfo: QueryResultInfo,
  locationId: string | undefined,
  locationData: WeaveLocation | undefined
) => {
  const { isLoading: merchantLoading, error: merchantError, isFetched: merchantFetched, refetch } = queryInfo;

  const activeProcessor = merchant?.processors?.find((processor: Processor) => processor.isActive === true);
  const isExpress = activeProcessor?.stripeAccountType === 'EXPRESS';
  const stripeId = activeProcessor?.stripeId;
  const hasStripeId = !!stripeId;
  const hasStripeOnboarded = !!activeProcessor?.stripeOnboarded;
  const hasStripePayments = hasStripeId && hasStripeOnboarded;
  const hasPayments = hasStripePayments;
  const stripeCreatedNotOnboarded = hasStripeId && !hasStripeOnboarded;
  const chargesEnabled = !!activeProcessor?.chargesEnabled;
  const payoutsEnabled = !!activeProcessor?.payoutsEnabled;
  const stripeLocationId = merchant?.terminalLocation;
  const daysSinceLastActivity = dayjs().diff(merchant?.lastActivityAt, 'day');
  const hasAccountAlert = daysSinceLastActivity >= 90;
  const hasFullPayments = merchant?.planName === 'Full' || merchant?.planName === 'FullPayments';
  const isDemoLocation = locationData?.opsType === 'DEMO';

  return {
    merchant,
    opsType: locationData?.opsType,
    merchantLoading,
    merchantFetched,
    merchantError,
    activeProcessor,
    isExpress,
    stripeId,
    hasStripeId,
    hasStripeOnboarded,
    daysSinceLastActivity,
    hasAccountAlert,
    hasStripePayments,
    hasPayments,
    hasFullPayments,
    stripeCreatedNotOnboarded,
    chargesEnabled,
    payoutsEnabled,
    locationId,
    stripeLocationId,
    isDemoLocation,
    refetch,
  };
};

const getEmptyMerchantInfo = (firstLocationId: string | undefined) =>
  getMerchantInfo(undefined, { isLoading: false, isFetched: false, refetch: () => {} }, firstLocationId, undefined);

export type MerchantData = ReturnType<typeof getMerchantInfo>;
export type MerchantsData = Record<string, MerchantData>;
type FeatureFlagResults = ReturnType<ReturnType<typeof useAppFlagStore>['getFeatureFlagLocationMap']>;

export const multiMerchantQueryKey = (locationTypeGroup: string[]) => [locationTypeGroup, paymentsQueryKeys.merchant];

export const getProdLocationsAtFirstIndex = (
  locationIdsByType: LocationIdsByType,
  forceProdPaymentsUrlFlags: FeatureFlagResults
) => {
  const modifiedLocationIdsByType: LocationIdsByType = {};
  const prodLocations: string[] = [];
  let locationType: keyof LocationIdsByType;
  for (locationType in locationIdsByType) {
    locationIdsByType[locationType]?.forEach((locationId) => {
      if (forceProdPaymentsUrlFlags[locationId]?.value || locationType === 'REGULAR') {
        prodLocations.push(locationId);
      } else {
        if (!modifiedLocationIdsByType[locationType]) {
          modifiedLocationIdsByType[locationType] = [];
        }
        modifiedLocationIdsByType[locationType]?.push(locationId);
      }
    });
  }

  return [prodLocations, ...Object.values(modifiedLocationIdsByType)];
};

/**
 * Hook to fetch merchants based on the provided location ID.
 * @param locationSelectionType - The type of location selection ('accessibleLocationIds', 'selectedLocationIds', a locationId string or an array of location IDs).
 */
export const useMerchants = (
  locationSelectionType: MerchantLocationSelectionType,
  { enabled = true }: UserMerchantsOptions = {}
) => {
  const queryClient = useQueryClient();
  const { accessibleLocationData } = useAppScopeStore();
  const queryLocationIds = useMerchantLocationIds(locationSelectionType);

  const merchantResult: MerchantResult = useQuery({
    queryKey: multiMerchantQueryKey(queryLocationIds ?? []),
    queryFn: () => getMerchants(queryLocationIds ?? []),
    enabled: enabled && !!queryLocationIds?.length,
    retryOnMount: false,
    select: (merchantData: Merchant[] | undefined) =>
      merchantData?.reduce((acc, merchant) => ({ ...acc, [merchant.id]: merchant }), {}) ?? {},
    ...getInterScreenQueryOptions(),
  });
  const { merchantsData, firstActiveMerchantData, isLoading } = useMemo(() => {
    let firstActiveMerchantData: MerchantData | undefined;
    const merchantsData: MerchantsData = {};
    const { data: merchantResultData, isLoading } = merchantResult;
    queryLocationIds.forEach((locationId) => {
      const merchantData = getMerchantInfo(
        merchantResultData?.[locationId],
        merchantResult,
        locationId,
        accessibleLocationData?.[locationId]
      );
      merchantsData[locationId] = merchantData;
      if (merchantData.hasPayments && !firstActiveMerchantData) firstActiveMerchantData = merchantData;
    });
    return { merchantsData, firstActiveMerchantData, isLoading };
  }, [merchantResult, accessibleLocationData]);

  const firstActiveMerchantLocationId = firstActiveMerchantData?.locationId;

  const isAWeaveUser = useMemo(() => isWeaveUser(), []);

  // rough debug tool for merchant data
  useEffect(() => {
    if (!isAWeaveUser) {
      return;
    }
    window.PaymentsMerchantDebug = {
      subtractLastActivityAtDays: (days: number) => {
        if (!firstActiveMerchantLocationId) return;

        const queryKey = multiMerchantQueryKey(queryLocationIds);
        queryClient.setQueryData(queryKey, (merchants: Merchant[] | undefined) => {
          if (!merchants) return [];
          const currentMerchant = merchants.find((merchant) => merchant.id === firstActiveMerchantLocationId);
          if (!currentMerchant) return merchants;

          const newLastActivityAt = dayjs(currentMerchant.lastActivityAt).subtract(days, 'day').toISOString();
          return merchants.map((merchant) =>
            merchant.id === firstActiveMerchantLocationId
              ? { ...merchant, lastActivityAt: newLastActivityAt }
              : merchant
          );
        });
      },
    };

    return () => {
      delete window.PaymentsMerchantDebug;
    };
  }, [merchantsData, firstActiveMerchantData]);

  const firstLocationId = queryLocationIds.find((locationId) => !!locationId);
  const firstLocation = (firstLocationId && merchantsData[firstLocationId]) || getEmptyMerchantInfo(firstLocationId);

  const merchantsInfo = useMemo(
    () =>
      Object.entries(merchantsData).reduce(
        (infoObj, [locationId, merchant], idx) => {
          const {
            hasPayments,
            merchantError,
            merchantLoading,
            merchantFetched,
            chargesEnabled,
            stripeLocationId,
            hasFullPayments,
            merchant: merchantObj,
          } = merchant;
          const merchantLoaded = !merchantLoading && merchantFetched;
          const {
            anyLocationHasPayments,
            hasMerchantsError,
            allMerchantsLoaded,
            locationsWithPayments,
            chargesEnabledLocations,
            chargesDisabledLocations,
            anyLocationHasTerminalPayments,
            anyLocationHasFullPayments,
          } = infoObj;

          infoObj.anyLocationHasPayments = !!anyLocationHasPayments || hasPayments;
          infoObj.hasMerchantsError = !!hasMerchantsError || !!merchantError;
          infoObj.allMerchantsLoaded = idx === 0 ? merchantLoaded : !!allMerchantsLoaded && merchantLoaded;
          infoObj.locationsWithPayments = hasPayments ? [...locationsWithPayments, locationId] : locationsWithPayments;
          infoObj.chargesEnabledLocations = chargesEnabled
            ? [...chargesEnabledLocations, locationId]
            : chargesEnabledLocations;
          infoObj.chargesDisabledLocations =
            hasPayments && !chargesEnabled ? [...chargesDisabledLocations, locationId] : chargesDisabledLocations;
          infoObj.anyLocationHasTerminalPayments = !!anyLocationHasTerminalPayments || !!stripeLocationId;
          infoObj.anyLocationHasFullPayments = !!anyLocationHasFullPayments || !!hasFullPayments;
          infoObj.payoutDescriptors = merchantObj?.payoutDescriptor
            ? [...infoObj.payoutDescriptors, merchantObj.payoutDescriptor]
            : infoObj.payoutDescriptors;
          return infoObj;
        },
        {
          allMerchantsLoaded: false,
          anyLocationHasPayments: false,
          hasMerchantsError: false,
          locationsWithPayments: [] as string[],
          chargesEnabledLocations: [] as string[],
          chargesDisabledLocations: [] as string[],
          anyLocationHasTerminalPayments: false,
          anyLocationHasFullPayments: false,
          payoutDescriptors: [] as string[],
        }
      ),
    [merchantsData]
  );

  const noLocationHasChargesEnabled =
    merchantsInfo.anyLocationHasPayments &&
    merchantsInfo.chargesDisabledLocations.length === merchantsInfo.locationsWithPayments.length;

  return {
    merchantsData,
    firstLocation,
    firstActiveMerchantLocationId,
    firstActiveMerchantData,
    isLoading,
    noLocationHasChargesEnabled,
    refetch: merchantResult.refetch,
    ...merchantsInfo,
  };
};

declare global {
  interface Window {
    PaymentsMerchantDebug?: PaymentsMerchantDebug;
  }
}

interface PaymentsMerchantDebug {
  subtractLastActivityAtDays: (days: number) => void;
}
